/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import "./styles/GenericTable.css";
import { ReactComponent as IconUpDown } from "../../shared/assets/Icons/IconsUpDown.svg";

// Ajuste: header ahora es string | React.ReactNode
export interface GenericTableColumn<T> {
  id?: string;
  header: string | React.ReactNode;
  accessor?: keyof T;
  sortAccessor?: (row: T) =>
    | string
    | number
    | Date
    | Array<string | number>
    | null
    | undefined;
  renderCell?: (
    row: T,
    rowIndex: number,
    toggleRowExpand?: (rowIndex: number) => void
  ) => React.ReactNode;
  fixed?: "left" | "right"; // New prop to indicate if column should be fixed
  width?: string; // New prop to specify column width
  sortable?: boolean;
}

export interface GenericTableProps<T> {
  columns: GenericTableColumn<T>[];
  data: T[];

  tableClassName?: string;
  initialRowsPerPage?: number;
  rowsPerPageOptions?: number[];
  width?: string; // Ej. "100%"
  maxWidth?: string;
  maxTableBodyHeight?: string;

  expandable?: boolean;
  renderExpanded?: (row: T) => React.ReactNode;
  renderExpandToggle?: (
    isExpanded: boolean,
    onClick: () => void,
    row: T
  ) => React.ReactNode;

  rowKey?: (row: T, index: number) => string | number;

  /**
   * NUEVO: Si deseas que la fila sea clickeable,
   * puedes pasar esta función. Se llamará al dar clic en una fila.
   */
  onRowClick?: (row: T, rowIndex: number) => void;

  /**
   * NUEVO: Si deseas resaltar filas seleccionadas,
   * pasa un Set con las keys de las filas (rowKey).
   */
  selectedRowKeys?: Set<string | number>;
  isFooterVisible?: boolean;
  overflow?: "visible" | "hidden"; // New prop for overflow control
}

type SortDirection = "asc" | "desc";

// Función auxiliar para determinar el "sortKey"
function getColumnSortKey<T>(col: GenericTableColumn<T>, index: number): string | null {
  if (col.id) return col.id;
  if (col.accessor) return String(col.accessor);
  if (typeof col.header === "string") return col.header;
  if (col.sortAccessor) return `__col_${index}`;
  return null;
}

type SortPrimitive = string | number | Date | null | undefined | Array<string | number>;

function normalizeSortValue(v: SortPrimitive): { kind: "nil" | "number" | "date" | "string"; value: number | string } {
  if (v == null) return { kind: "nil", value: 0 };

  // Arrays => join as a single comparable string
  if (Array.isArray(v)) {
    return { kind: "string", value: v.map(x => String(x)).join(" ").toLowerCase() };
  }

  if (v instanceof Date && !isNaN(v.getTime())) {
    return { kind: "date", value: v.getTime() };
  }

  if (typeof v === "number" && Number.isFinite(v)) {
    return { kind: "number", value: v };
  }

  // Try to coerce numeric-looking strings
  const s = String(v).trim();
  const maybeNum = Number(s.replace(/,/g, ""));
  if (!Number.isNaN(maybeNum) && s !== "") {
    return { kind: "number", value: maybeNum };
  }

  return { kind: "string", value: s.toLowerCase() };
}

function compareSortValues(a: SortPrimitive, b: SortPrimitive): number {
  const A = normalizeSortValue(a);
  const B = normalizeSortValue(b);

  // nils always last (in ascending)
  if (A.kind === "nil" && B.kind === "nil") return 0;
  if (A.kind === "nil") return 1;
  if (B.kind === "nil") return -1;

  // numbers and dates compare numerically
  if ((A.kind === "number" || A.kind === "date") && (B.kind === "number" || B.kind === "date")) {
    return (A.value as number) - (B.value as number);
  }

  // strings compare lexicographically (case-insensitive already)
  return String(A.value).localeCompare(String(B.value), undefined, { numeric: true, sensitivity: "base" });
}


export function GenericTable<T extends object>({
  columns,
  data,
  tableClassName = "",
  initialRowsPerPage = 10,
  rowsPerPageOptions = [5, 10, 20, 50],
  width = "100%",
  maxWidth,
  maxTableBodyHeight = "400px",
  expandable = false,
  renderExpanded,
  renderExpandToggle,
  rowKey,
  onRowClick,
  selectedRowKeys,
  isFooterVisible = true,
  overflow = "hidden", // Default to hidden for backward compatibility
}: GenericTableProps<T>) {
  // ============== Paginación ==============
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  // ============== Ordenamiento ==============
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // ============== Filas expandidas ==============
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const handleSort = (col: GenericTableColumn<T>, index: number) => {
    const sortKey = getColumnSortKey(col, index);
    if (!sortKey) return;
  
    if (sortColumn === sortKey) {
      setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(sortKey);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  // Ordenar data
  const sortedData = [...data];
if (sortColumn) {
  // find the column using the same key generation the header used
  const colIndex = columns.findIndex((c, idx) => getColumnSortKey(c, idx) === sortColumn);
  const colDef = columns[colIndex];

  if (colDef && (colDef.accessor || colDef.sortAccessor)) {
    sortedData.sort((a, b) => {
      const getVal = (row: T): SortPrimitive => {
        if (colDef.sortAccessor) return colDef.sortAccessor(row);
        if (colDef.accessor) return (row as any)[colDef.accessor];
        return null;
      };

      const result = compareSortValues(getVal(a), getVal(b));
      return sortDirection === "asc" ? result : -result;
    });
  }
}

  // Paginación
  const totalRows = sortedData.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);

  // Keep currentPage within valid bounds whenever rowsPerPage or data length changes
useEffect(() => {
  // totalPages can be 0; force at least page 1 for the UI
  const safeTotalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  setCurrentPage((p) => Math.min(p, safeTotalPages));
}, [totalRows, rowsPerPage]);

  const handleFirstPage = () => setCurrentPage(1);
  const handlePreviousPage = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNextPage = () =>
    setCurrentPage((p) => Math.min(totalPages, p + 1));
  const handleLastPage = () => setCurrentPage(totalPages);

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);

  // Expand/collapse
  const toggleRowExpand = (rowIndex: number) => {
    const newSet = new Set(expandedRows);
    if (newSet.has(rowIndex)) {
      newSet.delete(rowIndex);
    } else {
      newSet.add(rowIndex);
    }
    setExpandedRows(newSet);
  };

  const colCount = columns.length + (expandable ? 1 : 0);
	const dynamicMaxHeight = rowsPerPage <= 5 ? maxTableBodyHeight : "none";
	const effectiveOverflow = rowsPerPage > 5 ? "visible" : overflow;

  return (
    <div className="w-full" style={{ maxWidth }}>
      <div
        className={`
                    border border-semantic-border-secondary
                    rounded-sm shadow-sm bg-semantic-bg-primary
                    overflow-x-auto thin-scroll relative
                `}
        style={{ width }}
      >
        {/* Contenedor de scroll vertical, también con thin-scroll */}
        <div
					className={`thin-scroll ${effectiveOverflow === "visible" ? "overflow-visible" : "overflow-y-auto"}`}
					style={{ maxHeight: dynamicMaxHeight }}
        >
          <table
            className={`table-auto min-w-max whitespace-nowrap border-separate relative ${tableClassName}`}
            style={{ borderSpacing: 0, width: "100%" }}
          >
            {/* Cabecera */}
            <thead>
              <tr>
                {columns.map((col, index) => {
                  const isSortable = Boolean(col.accessor || col.sortAccessor);
                  const colKey = getColumnSortKey(col, index);
                  const isActiveSort = colKey && sortColumn === colKey;

                  let svgClasses = "w-3 h-3 text-gray-400 transition-transform";
                  if (isActiveSort) {
                    svgClasses = `
													w-3 h-3
													text-pink-600
													transition-transform
													${
														sortDirection === "desc"
															? "rotate-180"
															: ""
													}
											`;
                  }
                  const thKey =
                    typeof col.header === "string"
                      ? col.header
                      : `col_${index}`;

                  const fixedStyles = col.fixed
                    ? {
                        position: "sticky",
                        [col.fixed]: 0,
                        zIndex: 20,
                        backgroundColor: "var(--color-semantic-bg-secondary)",
                        width: col.width || "auto",
                        ...(col.fixed === "right"
                          ? {
                              borderLeft:
                                "1px solid var(--semantic-border-secondary)",
                            }
                          : {}),
                        ...(col.fixed === "left"
                          ? {
                              borderRight:
                                "1px solid var(--semantic-border-secondary)",
                            }
                          : {}),
                      }
                    : {};

                  return (
                    <th
                      key={thKey}
                      className={`
														px-4 py-2
														text-left text-sm font-semibold
														text-semantic-text-secondary
														border-b border-semantic-border-secondary
														select-none bg-semantic-bg-primary
														sticky top-0
														${
															isSortable
																? "cursor-pointer"
																: ""
														}
														${
															col.fixed
																? "bg-semantic-bg-secondary"
																: ""
														}
												`}
                      style={fixedStyles}
                      onClick={() => isSortable && handleSort(col, index)}
                    >
                      <div className="flex items-center gap-1">
                        <span>{col.header}</span>
                        {isSortable && <IconUpDown className={svgClasses} />}
                      </div>
                    </th>
                  );
                })}
                {expandable && (
                  <th
                    className="
													px-4 py-2
													text-left text-sm font-semibold
													text-semantic-text-secondary
													border-b border-semantic-border-secondary
													bg-semantic-bg-primary
													sticky top-0 z-10
											"
                  >
                    {/* Col extra para expand/collapse */}
                  </th>
                )}
              </tr>
            </thead>

            {/* Cuerpo */}
            <tbody>
              {currentData.length === 0 ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="
												px-4 py-2 text-center text-sm
												text-semantic-text-primary border-b
										"
                  >
                    No Data Available
                  </td>
                </tr>
              ) : (
                currentData.map((row, i) => {
                  const rowIndex = startIndex + i;
                  const isExpanded = expandedRows.has(rowIndex);
                  const keyForRow = rowKey ? rowKey(row, rowIndex) : rowIndex;
                  const isSelected = selectedRowKeys?.has(keyForRow);

                  return (
                    <React.Fragment key={keyForRow}>
                      <tr
                        onClick={() => onRowClick?.(row, rowIndex)}
												className={`
														transition-colors
														hover:bg-semantic-state-active
														${
															onRowClick
																? "cursor-pointer"
																: ""
														}
														${
															isSelected
																? "bg-semantic-state-active"
																: ""
														}
												`}
                      >
                        {columns.map((col, colIdx) => {
                          const cellKey =
                            typeof col.header === "string"
                              ? col.header
                              : `col_${colIdx}`;

                          const fixedStyles = col.fixed
                            ? {
                                position: "sticky",
                                [col.fixed]: 0,
                                zIndex: 10,
                                backgroundColor:
                                  "var(--color-semantic-bg-primary)",
                                width: col.width || "auto",
                                ...(col.fixed === "right"
                                  ? {
                                      borderLeft:
                                        "1px solid var(--semantic-border-secondary)",
                                    }
                                  : {}),
                                ...(col.fixed === "left"
                                  ? {
                                      borderRight:
                                        "1px solid var(--semantic-border-secondary)",
                                    }
                                  : {}),
                              }
                            : {};

                          if (col.renderCell) {
                            return (
                              <td
                                key={cellKey}
                                className={`
																		px-4 py-2
																		text-sm text-semantic-text-primary
																		border-b border-semantic-border-secondary
																		${
																			col.fixed
																				? "bg-white"
																				: ""
																		}
																`}
                                style={fixedStyles}
                              >
                                {col.renderCell(row, rowIndex, toggleRowExpand)}
                              </td>
                            );
                          }

                          if (col.accessor) {
                            return (
                              <td
                                key={cellKey}
                                className={`
																			px-4 py-2
																			text-sm text-semantic-text-primary
																			border-b border-semantic-border-secondary
																			${
																				col.fixed
																					? "bg-white"
																					: ""
																			}
																	`}
                                style={fixedStyles}
                              >
                                {String(row[col.accessor])}
                              </td>
                            );
                          }

                          // Sin accessor ni renderCell => celda vacía
                          return (
                            <td
                              key={cellKey}
                              className={`
																	px-4 py-2
																	text-sm text-semantic-text-primary
																	border-b border-semantic-border-secondary
																	${
																		col.fixed
																			? "bg-white"
																			: ""
																	}
															`}
                              style={fixedStyles}
                            />
                          );
                        })}

                        {expandable && (
                          <td
                            className="
																px-4 py-2
																border-b border-semantic-border-secondary
														"
                          >
                            {renderExpandToggle ? (
                              renderExpandToggle(
                                isExpanded,
                                () => toggleRowExpand(rowIndex),
                                row
                              )
                            ) : (
                              <button
                                className="
																		px-2 py-1
																		bg-transparent
																		hover:bg-semantic-state-active
																		rounded
																		text-xs font-medium
																"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleRowExpand(rowIndex);
                                }}
                              >
                                {isExpanded ? "–" : "+"}
                              </button>
                            )}
                          </td>
                        )}
                      </tr>

                      {/* Fila expandida */}
                      {expandable && isExpanded && renderExpanded && (
                        <tr>
                          <td
                            colSpan={colCount}
                            className="
																border-b border-semantic-border-secondary
																px-4 py-1
														"
                          >
                            {renderExpanded(row)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer => paginación */}
        {isFooterVisible && (
          <div className="border-t border-semantic-border-secondary bg-semantic-bg-primary">
            <div className="px-4 py-3">
              <div className="flex items-center justify-between">
                <span className="text-semantic-text-primary text-sm">
                  Total : {totalRows}
                </span>

                <div className="flex items-center justify-end gap-2 text-semantic-text-primary">
                  <button
                    className="border border-semantic-border-secondary rounded-md px-3 py-1 hover:bg-semantic-state-active disabled:opacity-50 text-sm"
                    onClick={handleFirstPage}
                    disabled={currentPage === 1}
                  >
                    <span className="text-base">&laquo;</span>
                  </button>

                  <button
                    className="border border-semantic-border-secondary rounded-md px-3 py-1 hover:bg-semantic-state-active disabled:opacity-50 text-sm"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                  >
                    <span className="text-base">&#8249;</span>
                  </button>

                  <span className="mx-1 text-sm">
                    {currentPage} of {totalPages || 1}
                  </span>

                  <button
                    className="border border-semantic-border-secondary rounded-md px-3 py-1 hover:bg-semantic-state-active disabled:opacity-50 text-sm"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages || totalPages === 0}
                  >
                    <span className="text-base">&#8250;</span>
                  </button>

                  <button
                    className="border border-semantic-border-secondary rounded-md px-3 py-1 hover:bg-semantic-state-active disabled:opacity-50 text-sm"
                    onClick={handleLastPage}
                    disabled={currentPage === totalPages || totalPages === 0}
                  >
                    <span className="text-base">&raquo;</span>
                  </button>

                  <select
                    className="border border-semantic-border-secondary rounded-md px-2 py-1 bg-semantic-bg-primary text-sm"
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                  >
                    {rowsPerPageOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                  <span className="text-sm">Rows per page</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
